// src/contexts/AddressBookContext.jsx
// Holds the personal user's saved addresses for the cross-app marketplace
// checkout. Addresses come from the personal login payload (fetched by
// invoice_generator from the mobile backend's `saved_addresses` model) and
// new/updated addresses are written back through the invoice_generator proxy.
import React, { createContext, useContext, useCallback, useMemo, useState } from "react";
import {
  getStoredAddresses,
  setStoredAddresses,
  savePersonalAddress,
  setDefaultPersonalAddress,
  fetchPersonalAddresses
} from "../utils/personalLogin";

const AddressBookContext = createContext(null);

export const useAddressBook = () => {
  const ctx = useContext(AddressBookContext);
  if (!ctx) throw new Error("useAddressBook must be used within AddressBookProvider");
  return ctx;
};

export const AddressBookProvider = ({ children }) => {
  const [addresses, setAddresses] = useState(() => getStoredAddresses() || []);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(() => {
    const list = getStoredAddresses() || [];
    return list.find((a) => a.isDefault) || list[0] || null;
  });

  const refreshAddresses = useCallback((list) => {
    const next = Array.isArray(list) ? list : getStoredAddresses() || [];
    setAddresses(next);
    setStoredAddresses(next);
    setSelectedAddress((current) => {
      if (current) {
        const updated = next.find((a) => a.id === current.id) || current;
        return updated;
      }
      return next.find((a) => a.isDefault) || next[0] || null;
    });
  }, []);

  // Fresh fetch from the server via the invoice_generator proxy. Falls back to
  // the stored list silently when not logged in or the backend is unreachable.
  const loadAddresses = useCallback(async () => {
    setLoadingAddresses(true);
    try {
      const list = await fetchPersonalAddresses();
      if (!Array.isArray(list)) return;
      setAddresses(list);
      setStoredAddresses(list);
      setSelectedAddress((current) => {
        if (current) {
          const updated = list.find((a) => a.id === current.id);
          if (updated) return updated;
        }
        return list.find((a) => a.isDefault) || list[0] || null;
      });
    } finally {
      setLoadingAddresses(false);
    }
  }, []);

  const saveNewAddress = useCallback(async (fields) => {
    const saved = await savePersonalAddress(fields);
    if (!saved) return null;
    const next = getStoredAddresses() || [];
    setAddresses(next);
    setStoredAddresses(next);
    setSelectedAddress((current) => {
      if (saved.isDefault) return saved;
      const updated = next.find((a) => a.id === current?.id);
      return updated || saved;
    });
    return saved;
  }, []);

  const chooseAddress = useCallback((address) => {
    setSelectedAddress(address);
    if (address && address.id && !address.isDefault) {
      setDefaultPersonalAddress(address.id);
    }
  }, []);

  const value = useMemo(
    () => ({
      addresses,
      selectedAddress,
      loadingAddresses,
      setSelectedAddress: chooseAddress,
      refreshAddresses,
      loadAddresses,
      saveNewAddress
    }),
    [addresses, selectedAddress, loadingAddresses, chooseAddress, refreshAddresses, loadAddresses, saveNewAddress]
  );

  return <AddressBookContext.Provider value={value}>{children}</AddressBookContext.Provider>;
};

export default AddressBookContext;
